import { useEffect, useState } from 'react';
import { SYSTEMS, familyOf, SYSCALL_FAMILIES, FAMILY_ORDER } from './palette';

// The landing's coloured pictures are drawn from the same generated history the
// dashboard reads (`npm run history` -> public/data). Nothing here is a typed
// number: each loader reduces one file to the handful of proportions a small
// figure needs, once, and every component that asks shares the result.
//
// These are the dashboard's own files, so fetching them here also warms the
// browser cache for anyone who goes on to open it.
const cache = new Map();
function load(file, reduce) {
  if (!cache.has(file)) {
    cache.set(file, fetch(`/data/${file}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(file))))
      .then(reduce));
  }
  return cache.get(file);
}

// Lines of code and crate count per subsystem, from each crate's current size.
const crates = () => load('history-crates.json', (data) => {
  const loc = {}, count = {};
  for (const crate of data.crates) {
    if (!crate.loc) continue;
    loc[crate.sys] = (loc[crate.sys] ?? 0) + crate.loc;
    if (crate.cargo) count[crate.sys] = (count[crate.sys] ?? 0) + 1;
  }
  const rows = SYSTEMS.map((s) => ({ ...s, loc: loc[s.id] ?? 0, crates: count[s.id] ?? 0 }));
  const totalLoc = rows.reduce((sum, r) => sum + r.loc, 0) || 1;
  const totalCrates = rows.reduce((sum, r) => sum + r.crates, 0) || 1;
  return rows.map((r) => ({ ...r, locShare: r.loc / totalLoc, crateShare: r.crates / totalCrates }));
});

// Commits per day, the whole life of the repository.
const activity = () => load('history-activity.json', (data) => ({ days: data.days, perDay: data.perDay, cumulative: data.cumulative }));

// The syscall table by family. `names` is the table as the ticker shows it.
const syscalls = () => load('history-syscalls.json', (data) => {
  const names = data.slots.filter((slot) => slot.gone == null).map((slot) => slot.name);
  const count = {};
  for (const name of names) { const f = familyOf(name); count[f] = (count[f] ?? 0) + 1; }
  const families = SYSCALL_FAMILIES
    .map((f) => ({ id: f.id, label: f.label, n: count[f.id] ?? 0, share: (count[f.id] ?? 0) / (names.length || 1) }))
    .filter((f) => f.n > 0)
    .sort((a, b) => FAMILY_ORDER.indexOf(a.id) - FAMILY_ORDER.indexOf(b.id));
  return { names, families };
});

const LOADERS = { crates, activity, syscalls };

// `useKernelData('crates')` -> the reduced rows, or null until they arrive.
// A failed fetch stays null: every figure has a drawing for "not yet".
export function useKernelData(which) {
  const [value, setValue] = useState(null);
  useEffect(() => {
    let alive = true;
    LOADERS[which]().then((v) => { if (alive) setValue(v); }, () => {});
    return () => { alive = false; };
  }, [which]);
  return value;
}
